/**
 * Bluetooth Thermal Printer — MXW01 Image Protocol
 * Uses mxw01-thermal-printer library for the proprietary MXW01/xofiz protocol.
 * This printer is IMAGE-based, not text-based. Everything is rendered
 * on a canvas and sent as pixel data.
 *
 * On native Android (Capacitor): uses CapacitorBLEAdapter for auto-connect.
 * On web browser: uses WebBluetoothAdapter (existing behavior).
 *
 * Usage:
 *   import printer from '@/utils/bluetooth-printer.js'
 *   await printer.connect()
 *   await printer.printTicket({ funcion, uuid, payment, price, date })
 */

import { ThermalPrinterClient, WebBluetoothAdapter } from 'mxw01-thermal-printer'
import { Capacitor } from '@capacitor/core'
import { CapacitorBLEAdapter } from '@/utils/capacitor-ble-adapter.js'

const PRINT_WIDTH = 384 // 58mm paper = 384 dots

// Logo for ticket header (inverted dancer figure, black on white)
const LOGO_DATA_URL = 'data:image/png;base64,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'

class MXW01Printer {
  constructor() {
    this.adapter = null
    this.client = null
    this.connected = false
    this.printerName = ''
  }

  isSupported() {
    // Always supported on native Android
    if (Capacitor.isNativePlatform()) return true
    return !!navigator.bluetooth
  }

  async connect() {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth no disponible')
    }

    // Pick the right adapter depending on platform
    this.adapter = Capacitor.isNativePlatform()
      ? new CapacitorBLEAdapter()
      : new WebBluetoothAdapter()

    this.client = new ThermalPrinterClient(this.adapter)

    this.client.on('connected', (event) => {
      this.connected = true
      this.printerName = event.device?.name || 'MXW01'
    })
    this.client.on('disconnected', () => {
      this.connected = false
    })
    this.client.on('error', (event) => {
      console.error('Printer error:', event.error)
    })

    await this.client.connect()
    this.connected = this.client.isConnected
    return this.connected
  }

  /**
   * Silent reconnect — reuses saved device ID, no picker shown
   */
  async reconnect() {
    if (this.connected) return true
    if (!Capacitor.isNativePlatform()) return false
    // Only works if we have a saved device ID
    if (!localStorage.getItem('mxw01_device_id')) return false
    try {
      if (!this.adapter) {
        this.adapter = new CapacitorBLEAdapter()
      }
      if (!this.client) {
        this.client = new ThermalPrinterClient(this.adapter)
        this.client.on('connected', (event) => {
          this.connected = true
          this.printerName = event.device?.name || 'MXW01'
        })
        this.client.on('disconnected', () => {
          this.connected = false
        })
        this.client.on('error', (event) => {
          console.error('Printer error:', event.error)
        })
      }
      await this.client.connect()
      this.connected = this.client.isConnected
      return this.connected
    } catch (e) {
      console.warn('[Printer] Silent reconnect failed:', e)
      return false
    }
  }

  async disconnect() {
    if (this.client) {
      await this.client.disconnect()
    }
    this.connected = false
  }

  /**
   * Print a ticket receipt
   */
  async printTicket(ticket) {
    if (!this.connected || !this.client) {
      throw new Error('Impresora no conectada')
    }
    const canvas = await this._renderTicket(ticket)
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    await this.client.print(imageData, {
      dither: 'threshold',
      brightness: 160,
      intensity: 75,
    })
  }

  /**
   * Print a test receipt
   */
async printTest() {
    if (!this.connected || !this.client) {
      throw new Error('Impresora no conectada')
    }
    const canvas = this._createCanvas(200)
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, PRINT_WIDTH, 200)
    ctx.fillStyle = '#000000'
    ctx.font = 'bold 24px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('GRUPO BABILONIA', PRINT_WIDTH / 2, 40)
    ctx.font = 'bold 16px Arial'
    ctx.fillText('Test de impresión', PRINT_WIDTH / 2, 70)
    this._drawDash(ctx, 90)
    ctx.font = 'bold 18px Arial'
    ctx.fillText('Impresora OK!', PRINT_WIDTH / 2, 120)
    ctx.font = 'bold 14px Arial'
    ctx.fillText(new Date().toLocaleString('es-UY'), PRINT_WIDTH / 2, 150)
    this._drawDash(ctx, 170)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    await this.client.print(imageData, {
      dither: 'threshold',
      brightness: 160,
      intensity: 75,
    })
  }

  /**
   * Render a ticket onto a canvas
   * Layout: [Logo | Text header] → BIG QR → Details
   */
  async _renderTicket(ticket) {
    const qrDataURL = await this._generateQRDataURL(ticket.uuid)
    const logoImg = await this._loadImage(LOGO_DATA_URL).catch(() => null)

    const maxHeight = 900
    const canvas = this._createCanvas(maxHeight)
    const ctx = canvas.getContext('2d')

    // Disable anti-aliasing for crisp black/white output
    ctx.imageSmoothingEnabled = false
    // White background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, PRINT_WIDTH, maxHeight)

    ctx.fillStyle = '#000000'
    let y = 10

    // ─── Header: Logo left, Text right (evenly distributed) ───
    const logoSize = 120
    const textX = logoSize + 16
    const textW = PRINT_WIDTH - textX - 5

    if (logoImg) {
      ctx.drawImage(logoImg, 6, y, logoSize, logoSize)
    }

    // 3 text lines evenly spaced across logo height, centered in text area
    const slot = logoSize / 4
    const textCenter = textX + (textW / 2)

    // "Grupo Babilonia" — bold, biggest
    ctx.font = 'bold 30px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('Grupo Babilonia', textCenter, y + slot + 8)

    // "Festival Arena y Barro"
    ctx.font = 'bold 20px Arial'
    ctx.fillText('Festival Arena y Barro', textCenter, y + slot * 2 + 6)

    // Show name
    ctx.font = 'bold 22px Arial'
    const lines = this._wrapText(ctx, ticket.funcion, textW)
    let showY = y + slot * 3 + 4
    for (const line of lines) {
      ctx.fillText(line, textCenter, showY)
      showY += 26
    }

    y += logoSize + 8

    // ─── Dash ───
    this._drawDash(ctx, y)
    y += 14

    // ─── BIG pure QR Code (360px — nearly full paper width) ───
    if (qrDataURL) {
      const qrImg = await this._loadImage(qrDataURL)
      const qrSize = 360
      const qrX = (PRINT_WIDTH - qrSize) / 2
      ctx.drawImage(qrImg, qrX, y, qrSize, qrSize)
      y += qrSize + 16
    }



    // ─── Payment + Price ───
    ctx.font = 'bold 20px Arial'
    ctx.textAlign = 'left'
    ctx.fillText(ticket.payment, 20, y + 20)
    ctx.textAlign = 'right'
    ctx.fillText('$' + ticket.price + ' UYU', PRINT_WIDTH - 20, y + 20)
    y += 32

    // ─── Date ───
    ctx.font = 'bold 18px Arial'
    ctx.textAlign = 'center'
    ctx.fillText(ticket.date, PRINT_WIDTH / 2, y + 16)
    y += 26

    // ─── Final separator ───
    this._drawDash(ctx, y)
    y += 14
    // Solid separator line
    ctx.save()
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 2
    ctx.setLineDash([])
    ctx.beginPath()
    ctx.moveTo(10, y)
    ctx.lineTo(PRINT_WIDTH - 10, y)
    ctx.stroke()
    ctx.restore()
    y += 14

    // Crop canvas to actual content height
    const cropped = this._createCanvas(y)
    const cropCtx = cropped.getContext('2d')
    cropCtx.imageSmoothingEnabled = false
    cropCtx.drawImage(canvas, 0, 0)

    // Force pure black/white — no gray pixels
    const imageData = cropCtx.getImageData(0, 0, cropped.width, cropped.height)
    const d = imageData.data
    for (let i = 0; i < d.length; i += 4) {
      const avg = (d[i] + d[i+1] + d[i+2]) / 3
      const bw = avg < 128 ? 0 : 255
      d[i] = d[i+1] = d[i+2] = bw
      d[i+3] = 255
    }
    cropCtx.putImageData(imageData, 0, 0)

    return cropped
  }

  // ─── Canvas Helpers ──────────────────────────
  _createCanvas(height) {
    const canvas = document.createElement('canvas')
    canvas.width = PRINT_WIDTH
    canvas.height = height
    return canvas
  }

  _drawDash(ctx, y) {
    ctx.save()
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 4])
    ctx.beginPath()
    ctx.moveTo(10, y)
    ctx.lineTo(PRINT_WIDTH - 10, y)
    ctx.stroke()
    ctx.restore()
  }

  _wrapText(ctx, text, maxWidth) {
    const words = text.split(' ')
    const lines = []
    let current = ''
    for (const word of words) {
      const test = current ? current + ' ' + word : word
      if (ctx.measureText(test).width > maxWidth) {
        if (current) lines.push(current)
        current = word
      } else {
        current = test
      }
    }
    if (current) lines.push(current)
    return lines
  }

  _loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = src
    })
  }

  /**
   * Generate a pure QR code as a data URL (no logo overlay)
   */
  _generateQRDataURL(text) {
    return new Promise((resolve) => {
      if (!window.QRCode) {
        console.warn('QRCode library not loaded')
        resolve(null)
        return
      }
      const container = document.createElement('div')
      container.style.position = 'fixed'
      container.style.left = '-9999px'
      document.body.appendChild(container)
      // Medium error correction — clean QR, max readability
      new window.QRCode(container, {
        text: text,
        width: 360,
        height: 360,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: window.QRCode.CorrectLevel.M,
      })
      setTimeout(() => {
        const qrCanvas = container.querySelector('canvas')
        const dataURL = qrCanvas ? qrCanvas.toDataURL('image/png') : null
        document.body.removeChild(container)
        resolve(dataURL)
      }, 100)
    })
  }
}

// Singleton
const printer = new MXW01Printer()
export default printer